import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/JobTracking.css';

interface Job {
  _id: string;
  customer: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  driver?: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    vehiclePlate: string;
    currentLocation?: {
      coordinates: [number, number];
    };
  };
  pickupLocation: {
    address: string;
    coordinates: [number, number];
  };
  dropoffLocation: {
    address: string;
    coordinates: [number, number];
  };
  vehicleDetails: {
    type: string;
    plateNumber?: string;
  };
  status: string;
  estimatedPrice: number;
  distance: number;
  createdAt: string;
  timeline?: Array<{
    status: string;
    timestamp: string;
  }>;
  pickupImages?: string[];
  dropoffImages?: string[];
}

const JobTracking: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [jobId]);

  useEffect(() => {
    // Auto-refresh for active jobs
    if (autoRefresh && job && (job.status === 'accepted' || job.status === 'in_progress')) {
      refreshIntervalRef.current = setInterval(() => {
        fetchJobDetails(true);
      }, 5000); // Refresh every 5 seconds

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, job]);

  const fetchJobDetails = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const response = await api.getJobById(jobId!);
      setJob(response.job);
      setError('');
    } catch (err: any) {
      console.error('Failed to load job:', err);
      setError('Failed to load job details');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'accepted': return '#3b82f6';
      case 'in_progress': return '#8b5cf6';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'accepted': return '✅';
      case 'in_progress': return '🚗';
      case 'completed': return '🎉';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const openGoogleMaps = (lat: number, lng: number, label: string) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const getDirections = () => {
    if (!job) return;

    const pickupLat = job.pickupLocation.coordinates[1];
    const pickupLng = job.pickupLocation.coordinates[0];
    const dropoffLat = job.dropoffLocation.coordinates[1];
    const dropoffLng = job.dropoffLocation.coordinates[0];

    const url = `https://www.google.com/maps/dir/?api=1&origin=${pickupLat},${pickupLng}&destination=${dropoffLat},${dropoffLng}`;
    window.open(url, '_blank');
  };

  const getImageUrl = (imagePath: string) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const imageUrl = `${API_BASE}/uploads/${imagePath}`.replace("api/uploads", "uploads");
    console.log('Image URL:', imageUrl);
    return `${imageUrl}`;
  };

  const downloadImage = async (imagePath: string, index: number) => {
    try {
      const imageUrl = getImageUrl(imagePath);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-${jobId}-image-${index + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download image:', error);
      alert('Failed to download image');
    }
  };

  const downloadAllImages = async (images: string[], prefix: string) => {
    for (let i = 0; i < images.length; i++) {
      await downloadImage(images[i], i);
      if (i < images.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  const openLightbox = (imagePath: string) => {
    setSelectedImage(getImageUrl(imagePath));
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setSelectedImage(null);
  };

  if (isLoading) {
    return (
      <div className="job-tracking-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-tracking-page">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <h2>{error || 'Job not found'}</h2>
          <button onClick={() => navigate('/jobs')} className="btn-back">
            ← Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="job-tracking-page">
      {/* Header */}
      <div className="tracking-header">
        <button onClick={() => navigate('/jobs')} className="btn-back-header">
          ← Back to Jobs
        </button>
        <h1>Job Tracking</h1>
        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button onClick={() => fetchJobDetails()} className="btn-refresh">
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className="status-banner" style={{ backgroundColor: getStatusColor(job.status) }}>
        <span className="status-icon">{getStatusIcon(job.status)}</span>
        <div className="status-info">
          <h2>Status: {job.status.toUpperCase().replace('_', ' ')}</h2>
          <p>Job ID: {job._id.slice(-8)}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="tracking-content">
        {/* Left Column - Details */}
        <div className="details-column">
          {/* Customer Info */}
          <div className="info-card">
            <h3>👤 Customer Information</h3>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{job.customer.firstName} {job.customer.lastName}</span>
            </div>
            <div className="info-row">
              <span className="label">Phone:</span>
              <a href={`tel:${job.customer.phone}`} className="value phone-link">
                {job.customer.phone}
              </a>
            </div>
          </div>

          {/* Driver Info */}
          {job.driver ? (
            <div className="info-card">
              <h3>🚗 Driver Information</h3>
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{job.driver.firstName} {job.driver.lastName}</span>
              </div>
              <div className="info-row">
                <span className="label">Phone:</span>
                <a href={`tel:${job.driver.phone}`} className="value phone-link">
                  {job.driver.phone}
                </a>
              </div>
              <div className="info-row">
                <span className="label">Vehicle:</span>
                <span className="value">{job.driver.vehiclePlate}</span>
              </div>
            </div>
          ) : (
            <div className="info-card warning">
              <h3>⚠️ No Driver Assigned</h3>
              <p>Waiting for driver to accept this job</p>
            </div>
          )}

          {/* Vehicle Details */}
          <div className="info-card">
            <h3>🚙 Vehicle to Tow</h3>
            <div className="info-row">
              <span className="label">Type:</span>
              <span className="value">{job.vehicleDetails.type}</span>
            </div>
            {job.vehicleDetails.plateNumber && (
              <div className="info-row">
                <span className="label">Plate:</span>
                <span className="value">{job.vehicleDetails.plateNumber}</span>
              </div>
            )}
          </div>

          {/* Locations */}
          <div className="info-card">
            <h3>📍 Locations</h3>
            <div className="location-item">
              <div className="location-header">
                <span className="location-label">Pickup</span>
                <button
                  onClick={() => openGoogleMaps(
                    job.pickupLocation.coordinates[1],
                    job.pickupLocation.coordinates[0],
                    'Pickup Location'
                  )}
                  className="btn-map-link"
                >
                  View on Map →
                </button>
              </div>
              <p className="location-address">{job.pickupLocation.address}</p>
            </div>
            <div className="location-divider">↓</div>
            <div className="location-item">
              <div className="location-header">
                <span className="location-label">Drop-off</span>
                <button
                  onClick={() => openGoogleMaps(
                    job.dropoffLocation.coordinates[1],
                    job.dropoffLocation.coordinates[0],
                    'Drop-off Location'
                  )}
                  className="btn-map-link"
                >
                  View on Map →
                </button>
              </div>
              <p className="location-address">{job.dropoffLocation.address}</p>
            </div>
            <button onClick={getDirections} className="btn-get-directions">
              🗺️ Get Directions (Pickup → Drop-off)
            </button>
          </div>

          {/* Job Details */}
          <div className="info-card">
            <h3>📊 Job Details</h3>
            <div className="info-row">
              <span className="label">Distance:</span>
              <span className="value">{job.distance.toFixed(1)} km</span>
            </div>
            <div className="info-row">
              <span className="label">Estimated Price:</span>
              <span className="value">₹{job.estimatedPrice}</span>
            </div>
            <div className="info-row">
              <span className="label">Created:</span>
              <span className="value">{formatDate(job.createdAt)}</span>
            </div>
          </div>

          {/* Job Images */}
          {(job.pickupImages && job.pickupImages.length > 0) || (job.dropoffImages && job.dropoffImages.length > 0) ? (
            <div className="info-card">
              <h3>📷 Job Images</h3>

              {/* Pickup Images */}
              {job.pickupImages && job.pickupImages.length > 0 && (
                <div className="image-section">
                  <div className="image-section-header">
                    <h4>Pickup Location Images ({job.pickupImages.length})</h4>
                    <button
                      onClick={() => downloadAllImages(job.pickupImages!, 'pickup')}
                      className="btn-download-all"
                    >
                      ⬇ Download All Pickup
                    </button>
                  </div>
                  <div className="image-grid">
                    {job.pickupImages.map((imagePath, index) => (
                      <div key={index} className="image-item">
                        <img
                          src={getImageUrl(imagePath)}
                          alt={`Pickup ${index + 1}`}
                          onClick={() => openLightbox(imagePath)}
                          className="job-image-thumbnail"
                        />
                        <button
                          onClick={() => downloadImage(imagePath, index)}
                          className="btn-download-image"
                        >
                          ⬇
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dropoff Images */}
              {job.dropoffImages && job.dropoffImages.length > 0 && (
                <div className="image-section" style={{ marginTop: job.pickupImages && job.pickupImages.length > 0 ? '20px' : '0' }}>
                  <div className="image-section-header">
                    <h4>Dropoff Location Images ({job.dropoffImages.length})</h4>
                    <button
                      onClick={() => downloadAllImages(job.dropoffImages!, 'dropoff')}
                      className="btn-download-all"
                    >
                      ⬇ Download All Dropoff
                    </button>
                  </div>
                  <div className="image-grid">
                    {job.dropoffImages.map((imagePath, index) => (
                      <div key={index} className="image-item">
                        <img
                          src={getImageUrl(imagePath)}
                          alt={`Dropoff ${index + 1}`}
                          onClick={() => openLightbox(imagePath)}
                          className="job-image-thumbnail"
                        />
                        <button
                          onClick={() => downloadImage(imagePath, index)}
                          className="btn-download-image"
                        >
                          ⬇
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Right Column - Timeline */}
        <div className="timeline-column">
          <div className="timeline-card">
            <h3>📅 Job Timeline</h3>
            {job.timeline && job.timeline.length > 0 ? (
              <div className="timeline">
                {job.timeline.map((event, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-dot" style={{ backgroundColor: getStatusColor(event.status) }}>
                      {getStatusIcon(event.status)}
                    </div>
                    <div className="timeline-content">
                      <h4>{event.status.toUpperCase().replace('_', ' ')}</h4>
                      <p>{formatDate(event.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-timeline">No timeline events yet</p>
            )}
          </div>

          {/* Map Placeholder */}
          <div className="map-card">
            <h3>🗺️ Map View</h3>
            <div className="map-placeholder">
              <p>🗺️</p>
              <p>Interactive map view</p>
              <small>Click location buttons to view on Google Maps</small>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              ✕
            </button>
            <img src={selectedImage} alt="Full size" className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobTracking;
